// Sprint 65.1 — Deal Baazar (customer).
//
// LIVE PRODUCT SYNC: campaigns store only {product_id, deal_price, tag, ...}.
// All product fields (name, image, mrp, selling_price, stock, variants,
// brand, rating, reviews, sold_count) are fetched live from /products at
// render time so any admin edit propagates automatically. Products with
// is_active=false are auto-hidden; stock=0 products stay in place with the
// OOS overlay + Notify-Me button (see DealProductCard).

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ChevronRight, Flame, ArrowLeft } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import DealProductCard from "@/components/customer/DealProductCard";
import DealCountdown from "@/components/customer/DealCountdown";
import {
  activeCampaigns, applyDealPrice, slugify, msRemaining,
} from "@/lib/dealPricing";

// Filter banners the same way we filter campaigns.
function activeBanners(list) {
  const now = Date.now();
  return (list || [])
    .filter((b) => b && b.is_active !== false)
    .filter((b) => {
      const s = b.start_date ? Date.parse(b.start_date) : null;
      const e = b.end_date   ? Date.parse(b.end_date)   : null;
      if (s && s > now) return false;
      if (e && e < now) return false;
      return true;
    })
    .sort((a, b) => (b.priority || 0) - (a.priority || 0));
}

// Load campaigns + banners from the public settings endpoint (no auth needed).
function useDealMarketing() {
  const [data, setData] = useState({ campaigns: [], banners: [], loading: true });

  useEffect(() => {
    let cancelled = false;
    api.get("/store/settings/public")
      .then((r) => {
        if (cancelled) return;
        const mkt = r.data?.marketing || {};
        setData({
          campaigns: Array.isArray(mkt.deal_campaigns) ? mkt.deal_campaigns : [],
          banners:   Array.isArray(mkt.deal_banners)   ? mkt.deal_banners   : [],
          loading: false,
        });
      })
      .catch(() => setData({ campaigns: [], banners: [], loading: false }));
    return () => { cancelled = true; };
  }, []);

  return data;
}

// Build a lightweight product object from a campaign line's snapshot.
// (Kept only for backward compatibility with old campaigns that still carry
// snapshot fields. New campaigns store just IDs and hydrate live.)
function productFromLine(line) {
  return {
    id: line.product_id,
    name: line.name || "",
    thumbnail_url: line.thumbnail_url || "",
    mrp: Number(line.mrp || line.selling_price || 0),
    selling_price: Number(line.selling_price || 0),
    weight: line.weight || "",
    weight_unit: line.weight_unit || "",
    stock: typeof line.stock === "number" ? line.stock : 999,
    is_active: true,
    has_variants: false,
  };
}

// Sprint 65.1 — Live product hydrator.
// Fetches every product_id referenced across active campaigns from /products.
// Runs only when a user session exists (matches the storefront's existing
// gated-fetch pattern used by Home.jsx / Shop.jsx). Returns a { id → product }
// map so callers can hydrate live name / image / MRP / stock / brand / etc.
function useLiveProducts(campaigns, user) {
  const [byId, setById] = useState({});
  const ids = useMemo(() => {
    const s = new Set();
    (campaigns || []).forEach((c) => (c.products || []).forEach((p) => p.product_id && s.add(p.product_id)));
    return [...s];
  }, [campaigns]);

  useEffect(() => {
    if (!user || !ids.length) { setById({}); return; }
    let cancelled = false;
    (async () => {
      // Try one bulk list first (limit=100), then fill missing via /products/:id.
      const map = {};
      try {
        const { data } = await api.get(`/products?limit=100&sort=-created_at`);
        (data?.items || []).forEach((p) => { if (p && p.id) map[p.id] = p; });
      } catch { /* ignore — fall through to per-id fetch */ }
      const missing = ids.filter((id) => !map[id]);
      await Promise.all(missing.map(async (id) => {
        try { const r = await api.get(`/products/${id}`); if (r.data?.id) map[id] = r.data; }
        catch { /* skip — product may be deleted; will be filtered out downstream */ }
      }));
      if (!cancelled) setById(map);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join("|"), !!user]);

  return byId;
}

// Hydrate a live product from its campaign line and apply the deal override.
// Returns null when the product is deleted / inactive (→ auto-remove).
function hydrateLive(line, campaign, liveById) {
  const live = liveById[line.product_id];
  if (!live) return null;                                   // not yet loaded or deleted
  if (live.is_active === false) return null;                // admin marked inactive → remove
  return applyDealPrice(live, campaign, line);
}

function DealHero({ campaigns }) {
  const [i, setI] = useState(0);
  const top = campaigns.slice(0, 5);
  useEffect(() => {
    if (top.length <= 1) return;
    const id = setInterval(() => setI((v) => (v + 1) % top.length), 4500);
    return () => clearInterval(id);
  }, [top.length]);
  if (!top.length) {
    return (
      <div className="relative w-full aspect-[16/6] md:aspect-[16/5] rounded-2xl overflow-hidden mb-4 grid place-items-center"
           style={{ background: "linear-gradient(135deg,#DC1E1E,#8B1E2E)" }}>
        <div className="text-center text-white p-6">
          <div className="inline-flex items-center gap-2 text-2xl md:text-4xl font-black tracking-tight">
            <Flame className="w-6 h-6 md:w-8 md:h-8 text-white drop-shadow" /> Deal Baazar
          </div>
          <div className="text-xs md:text-sm opacity-90 mt-1">Exclusive campaigns dropping soon.</div>
        </div>
      </div>
    );
  }
  const c = top[i];
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const img = (isMobile ? c.banner_mobile : c.banner_desktop) || c.hero_banner || c.banner_desktop || c.banner_mobile;
  return (
    <Link
      to={`/deal-baazar/${c.slug || slugify(c.name)}`}
      className="relative block w-full aspect-[16/6] md:aspect-[16/5] rounded-2xl overflow-hidden mb-4"
      style={{ background: c.theme_color || "#DC1E1E" }}
      data-testid="deal-hero"
    >
      {img && <img src={img} alt={c.name} className="absolute inset-0 w-full h-full object-cover" />}
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/25 to-transparent" />
      <div className="absolute inset-0 flex flex-col justify-end p-4 md:p-8 text-white">
        <div className="text-xs md:text-sm opacity-90 font-bold uppercase tracking-widest">Exclusive Deal</div>
        <div className="text-2xl md:text-5xl font-black leading-tight max-w-xl">{c.name}</div>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <DealCountdown campaign={c} />
          <span className="inline-flex items-center gap-1 rounded-full bg-white text-black text-[11px] md:text-sm font-bold px-2.5 md:px-3 py-1 shadow">
            Shop Now <ChevronRight className="w-3 h-3" />
          </span>
        </div>
      </div>
      {top.length > 1 && (
        <div className="absolute bottom-2 right-3 flex gap-1">
          {top.map((_, idx) => (
            <span key={idx} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-white" : "w-2 bg-white/50"}`} />
          ))}
        </div>
      )}
    </Link>
  );
}

function CampaignRail({ campaign, liveById, showCount = 8 }) {
  const items = (campaign.products || [])
    .map((line) => hydrateLive(line, campaign, liveById))
    .filter((p) => p && p.id)
    .slice(0, showCount);

  if (!items.length) return null;

  const theme = campaign.theme_color || "#DC1E1E";
  return (
    <section className="my-6" data-testid={`deal-rail-${campaign.slug || campaign.id}`}>
      <div
        className="flex items-center justify-between mb-2 rounded-xl px-3 py-2 text-white"
        style={{ background: `linear-gradient(90deg, ${theme}, ${theme}CC)` }}
      >
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest opacity-90 font-bold">Deal Baazar</div>
          <h2 className="text-lg md:text-2xl font-black leading-tight truncate">{campaign.name}</h2>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {campaign.show_timer !== false && <DealCountdown campaign={campaign} size="sm" />}
          <Link
            to={`/deal-baazar/${campaign.slug || slugify(campaign.name)}`}
            className="inline-flex items-center gap-0.5 rounded-full bg-white/95 hover:bg-white text-black text-[11px] font-bold px-2.5 py-1 shadow"
            data-testid={`deal-rail-see-all-${campaign.slug || campaign.id}`}
          >
            See all <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
        {items.map((p) => (
          <DealProductCard key={p.id} product={p} campaign={campaign} />
        ))}
      </div>
    </section>
  );
}

function StandaloneBanner({ banner }) {
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const img = (isMobile ? banner.banner_mobile : banner.banner_desktop) || banner.banner_desktop || banner.banner_mobile;
  if (!img) return null;
  const inner = (
    <div className={`relative w-full ${isMobile ? "aspect-[16/8]" : "aspect-[16/5]"} rounded-2xl overflow-hidden my-4`}>
      <img src={img} alt={banner.title || "Deal banner"} className="w-full h-full object-cover" />
      {(banner.title || banner.subtitle) && (
        <div className="absolute inset-0 flex flex-col justify-end p-4 md:p-6 bg-gradient-to-t from-black/70 to-transparent text-white">
          {banner.title && <div className="font-black text-xl md:text-3xl leading-tight">{banner.title}</div>}
          {banner.subtitle && <div className="text-xs md:text-sm opacity-90">{banner.subtitle}</div>}
        </div>
      )}
    </div>
  );
  return banner.link_to ? <Link to={banner.link_to} data-testid={`deal-banner-${banner.id}`}>{inner}</Link> : inner;
}

// ─── Root landing ─────────────────────────────────────────────────────────
function DealBazaarLanding({ campaigns, banners, liveById, loading }) {
  const bannerList = activeBanners(banners);
  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="w-full aspect-[16/6] rounded-2xl" />
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-40 w-full rounded-2xl" />
      </div>
    );
  }
  if (!campaigns.length) {
    return (
      <div className="text-center py-20">
        <Flame className="w-14 h-14 mx-auto text-primary" />
        <div className="mt-3 text-xl font-bold">The Deal Baazar is warming up</div>
        <div className="text-sm text-muted-foreground max-w-md mx-auto mt-1">
          Big-ticket deals will land here shortly. Come back soon or explore our shop for today's live discounts.
        </div>
        <Link to="/shop" className="inline-flex items-center gap-1 mt-4 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-bold">
          Continue Shopping <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  const nodes = [];
  campaigns.forEach((c, i) => {
    // Interleave banners between rails when available
    if (bannerList[i]) nodes.push(<StandaloneBanner key={`b-${bannerList[i].id || i}`} banner={bannerList[i]} />);
    nodes.push(<CampaignRail key={c.id || c.slug || i} campaign={c} liveById={liveById} />);
  });

  return <>{nodes}</>;
}

// ─── Individual campaign page (/deal-baazar/:slug) ────────────────────────
function CampaignDetail({ campaign, liveById }) {
  const nav = useNavigate();
  const theme = campaign.theme_color || "#DC1E1E";
  const items = (campaign.products || [])
    .map((line) => hydrateLive(line, campaign, liveById))
    .filter((p) => p && p.id);

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const img = (isMobile ? campaign.banner_mobile : campaign.banner_desktop) || campaign.hero_banner || campaign.banner_desktop || campaign.banner_mobile;
  const ms = msRemaining(campaign);

  return (
    <div>
      <button
        onClick={() => nav("/deal-baazar")}
        className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground mb-3"
        data-testid="deal-campaign-back"
      >
        <ArrowLeft className="w-4 h-4" /> Deal Baazar
      </button>

      <div
        className="relative w-full aspect-[16/6] md:aspect-[16/5] rounded-2xl overflow-hidden mb-5"
        style={{ background: theme }}
      >
        {img && <img src={img} alt={campaign.name} className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-end p-4 md:p-8 text-white">
          <div className="text-xs md:text-sm opacity-90 font-bold uppercase tracking-widest">Exclusive Deal</div>
          <div className="text-2xl md:text-5xl font-black leading-tight">{campaign.name}</div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {campaign.show_timer !== false && <DealCountdown campaign={campaign} size="lg" />}
            {items.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/95 text-black text-xs md:text-sm font-bold px-3 py-1">
                {items.length} deal{items.length === 1 ? "" : "s"} live
              </span>
            )}
          </div>
        </div>
      </div>

      {ms === 0 ? (
        <div className="rounded-2xl border-2 border-dashed p-8 text-center text-muted-foreground">
          This campaign has ended. Head back to the Deal Baazar for live drops.
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed p-8 text-center text-muted-foreground">
          Products for this campaign are being lined up. Check back shortly.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
          {items.map((p) => <DealProductCard key={p.id} product={p} campaign={campaign} />)}
        </div>
      )}
    </div>
  );
}

// ─── Container ────────────────────────────────────────────────────────────
export default function DealBazaar() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { campaigns, banners, loading } = useDealMarketing();
  const visibleCampaigns = useMemo(() => activeCampaigns(campaigns), [campaigns]);
  const liveById = useLiveProducts(visibleCampaigns, user);

  const detail = slug
    ? visibleCampaigns.find((c) => (c.slug || slugify(c.name)) === slug)
    : null;

  return (
    <div className="max-w-6xl mx-auto px-3 md:px-6 py-3 md:py-6" data-testid="deal-bazaar-page">
      {slug ? (
        loading ? (
          <Skeleton className="w-full aspect-[16/6] rounded-2xl" />
        ) : detail ? (
          <CampaignDetail campaign={detail} liveById={liveById} />
        ) : (
          <div className="text-center py-16">
            <div className="text-lg font-bold">Campaign not found or expired</div>
            <Link to="/deal-baazar" className="inline-flex items-center gap-1 mt-3 text-primary font-semibold">
              Back to Deal Baazar <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        )
      ) : (
        <>
          <DealHero campaigns={visibleCampaigns} />
          <DealBazaarLanding campaigns={visibleCampaigns} banners={banners} liveById={liveById} loading={loading} />
        </>
      )}
    </div>
  );
}
